import { useCallback, useEffect, useState } from 'react';
import type { LedgerSummary, RequirementProgressItem, RequirementProgressResponse } from '@starter/shared';
import { api, ApiError } from '../../api/client.js';
import { EmptyState } from '../../components/EmptyState.js';
import { ErrorBanner } from '../../components/ErrorBanner.js';
import { LoadingState } from '../../components/LoadingState.js';
import { PageHeader } from '../../components/PageHeader.js';
import { StatusBadge } from '../../components/StatusBadge.js';
import {
  requirementProgressBarPercent,
  requirementProgressIsIndeterminate,
  requirementProgressShowsBar,
  requirementProgressStateLabel,
  requirementProgressStateVariant,
  requirementProgressThresholdText,
  requirementRuleLabel,
} from '../../lib/requirementProgressLabels.js';

function RequirementProgressCard({ item }: { item: RequirementProgressItem }) {
  const indeterminate = requirementProgressIsIndeterminate(item);
  const showBar = requirementProgressShowsBar(item);
  const thresholdText = requirementProgressThresholdText(item);

  return (
    <article className="requirement-progress-item" data-rule-type={item.ruleType}>
      <div className="requirement-progress-item__header">
        <h3 className="requirement-progress-item__title">{requirementRuleLabel(item.ruleType)}</h3>
        <StatusBadge
          status={requirementProgressStateLabel(item)}
          variant={requirementProgressStateVariant(item)}
        />
      </div>
      <div className="requirement-progress-item__stats">
        <span>
          <strong>Current:</strong> {item.current}
        </span>
        {thresholdText && <span>{thresholdText}</span>}
        {!indeterminate && item.remaining !== null && (
          <span>
            <strong>Remaining:</strong> {item.remaining}
          </span>
        )}
      </div>
      {showBar && (
        <div
          className="requirement-progress-bar"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={item.threshold ?? 0}
          aria-valuenow={Math.min(item.current, item.threshold ?? item.current)}
          aria-label={`${requirementRuleLabel(item.ruleType)} progress`}
        >
          <div
            className="requirement-progress-bar__fill"
            style={{ width: `${requirementProgressBarPercent(item)}%` }}
          />
        </div>
      )}
      {indeterminate && (
        <p className="text-muted text-sm">
          This requirement cannot be evaluated automatically yet — confirm with your programme director.
        </p>
      )}
    </article>
  );
}

function LedgerBreakdown({ summary }: { summary: LedgerSummary }) {
  const specialtyEntries = Object.entries(summary.countsBySpecialty);
  const roleEntries = Object.entries(summary.countsByRole);
  if (specialtyEntries.length === 0 && roleEntries.length === 0) return null;

  return (
    <div className="card">
      <h3 className="card__title">Approved case breakdown</h3>
      <div className="stat-grid stat-grid--wide">
        {specialtyEntries.map(([name, count]) => (
          <div key={`spec-${name}`} className="stat-card">
            <p className="stat-card__label">{name}</p>
            <p className="stat-card__value">{count}</p>
          </div>
        ))}
        {roleEntries.map(([name, count]) => (
          <div key={`role-${name}`} className="stat-card">
            <p className="stat-card__label">{name.replace(/_/g, ' ')}</p>
            <p className="stat-card__value">{count}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ProgressPage() {
  const [data, setData] = useState<RequirementProgressResponse | null>(null);
  const [ledgerSummary, setLedgerSummary] = useState<LedgerSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);

  const load = useCallback(() => {
    setError(null);
    setForbidden(false);
    return Promise.all([api.getRequirementProgress(), api.getAcademicLedgerSummary()])
      .then(([progressRes, ledgerRes]) => {
        setData(progressRes);
        setLedgerSummary(ledgerRes.summary);
      })
      .catch((err) => {
        if (err instanceof ApiError && err.status === 403) {
          setForbidden(true);
          setError('You do not have access to requirement progress for this account.');
          return;
        }
        setError(err instanceof ApiError ? err.message : 'Failed to load requirement progress');
      });
  }, []);

  useEffect(() => {
    setLoading(true);
    load().finally(() => setLoading(false));
  }, [load]);

  return (
    <div className="page">
      <PageHeader title="Progress" subtitle="Requirement completion at a glance" />

      {error && !loading && (
        <ErrorBanner message={error} onRetry={forbidden ? undefined : () => void load()} />
      )}

      {loading ? (
        <LoadingState message="Loading requirement progress…" />
      ) : !error && data?.reason === 'NO_ACTIVE_REQUIREMENT_PLAN' ? (
        <div className="card">
          <EmptyState
            icon={<span aria-hidden>📋</span>}
            title="No requirement plan assigned"
            description="Your cohort does not have an active requirement plan yet. Once your programme director assigns one, you'll see minimums, caps, and progress here."
          />
          {data.totalApprovedCases > 0 && (
            <p className="text-muted text-sm">
              You have {data.totalApprovedCases} approved case(s) on your ledger so far.
            </p>
          )}
        </div>
      ) : !error && data && data.progress.length === 0 ? (
        <div className="card">
          <EmptyState
            icon={<span aria-hidden>📊</span>}
            title="No requirement rules to show"
            description="An active plan is assigned but has no evaluable rules yet."
          />
        </div>
      ) : !error && data ? (
        <>
          <div className="card">
            <div className="stat-grid">
              <div className="stat-card">
                <p className="stat-card__label">Total approved cases</p>
                <p className="stat-card__value">{data.totalApprovedCases}</p>
              </div>
            </div>
          </div>
          {ledgerSummary && <LedgerBreakdown summary={ledgerSummary} />}
          <div className="card">
            <h3 className="card__title">Requirement rules</h3>
            <div className="requirement-progress-list">
              {data.progress.map((item) => (
                <RequirementProgressCard key={item.ruleId} item={item} />
              ))}
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
